import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, GripVertical, FileText, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EnhancedColorPicker } from "./EnhancedColorPicker";
import { useFormQuestionsData } from "@/hooks/useFormRelationships";
import { MatrixRowSelector } from "@/components/MatrixRowSelector";
import { useCalculations } from "@/hooks/useCalculations";
import { HierarchicalGroupSelector } from "./HierarchicalGroupSelector";

// Tipo de valor que puede ser nuevo formato (objeto) o antiguo formato (número/string directo)
type CalculationValue = {
  source: "calculation";
  id: number | string | null;
};

type ValueFormat =
  | { type: "number" | "form_variable"; value: string }
  | { type: "calculation"; value: string | CalculationValue }
  | number
  | string;

interface RadialDimension {
  name: string;
  barValue: ValueFormat;
  lineValue: ValueFormat;
  // Campos avanzados para integración con formularios
  barValueFormSource?: "main" | "referral";
  lineValueFormSource?: "main" | "referral";
  barValueMatrixRowId?: string;
  barValueMatrixRowText?: string;
  barValueMatrixColumnId?: string;
  barValueMatrixColumnText?: string;
  lineValueMatrixRowId?: string;
  lineValueMatrixRowText?: string;
  lineValueMatrixColumnId?: string;
  lineValueMatrixColumnText?: string;
  // Para selección jerárquica de grupos
  barValueSelectedGroup?: string;
  barValueSelectedQuestion?: string;
  lineValueSelectedGroup?: string;
  lineValueSelectedQuestion?: string;
}

interface RadialCategory {
  name: string;
  color: string;
  dimensions: RadialDimension[];
}

interface RadialChartConfigProps {
  data: RadialCategory[];
  onChange: (data: RadialCategory[]) => void;
  templateId: number;
  jotformFormId?: string | null;
  jotformFormIdReferral?: string | null;
}

interface NormalizedValue {
  type: string;
  value: string;
  rawValue: any;
}

// Función para normalizar valores del formato antiguo al nuevo
function normalizeValue(value: ValueFormat): NormalizedValue {
  if (typeof value === "object" && value !== null && "type" in value) {
    const typedValue = value as any;
    const rawValue = typedValue.value;

    if (typedValue.type === "calculation") {
      if (rawValue && typeof rawValue === "object" && "id" in rawValue) {
        const idValue = rawValue.id;
        return {
          type: typedValue.type,
          value: idValue != null ? String(idValue) : "",
          rawValue,
        };
      }
      return {
        type: typedValue.type,
        value: rawValue != null ? String(rawValue) : "",
        rawValue,
      };
    }

    return {
      type: typedValue.type,
      value: rawValue != null ? String(rawValue) : "",
      rawValue,
    };
  }
  // Formato antiguo: convertir a formato nuevo
  return {
    type: "number",
    value: String(value || ""),
    rawValue: value,
  };
}

// Función para obtener el tipo y valor de un ValueFormat
function getValueParts(value: ValueFormat): NormalizedValue {
  return normalizeValue(value);
}

export function RadialChartConfig({ data = [], onChange, templateId, jotformFormId, jotformFormIdReferral }: RadialChartConfigProps) {
  // Hook para obtener datos de ambos formularios
  const mainFormData = useFormQuestionsData(jotformFormId);
  const referralFormData = useFormQuestionsData(jotformFormIdReferral);
  
  // Hook para obtener cálculos disponibles
  const { calculations } = useCalculations(templateId);

  // Combinar los datos de ambos formularios (si existen)
  const formsData: any[] = [];
  if (Array.isArray(mainFormData.formsData)) {
    formsData.push(...mainFormData.formsData.map((form: any) => ({ ...form, isReferral: false })));
  }
  if (Array.isArray(referralFormData.formsData)) {
    formsData.push(...referralFormData.formsData.map((form: any) => ({ ...form, isReferral: true })));
  }

  // IDs de formularios seleccionados por fuente
  const mainFormId = formsData.find(f => !f.isReferral)?.formId || jotformFormId || "";
  const referralFormId = formsData.find(f => f.isReferral)?.formId || jotformFormIdReferral || "";

  // Function to check if a question is a matrix question
  const isMatrixQuestion = (questionName: string, formSource: "main" | "referral" = "main") => {
    const currentFormData = formSource === "main" ? mainFormData : referralFormData;
    const formsData = currentFormData.formsData || [];
    
    const targetForm = formsData.find(form => 
      formSource === "main" ? !form.isReferral : form.isReferral
    );

    if (!targetForm || !targetForm.questions) {
      return false;
    }

    const question = targetForm.questions.find(
      (q: any) => q.name === questionName || q.qid === questionName
    );

    return question?.type === 'control_matrix';
  };
  const handleAddCategory = () => {
    const newCategory: RadialCategory = {
      name: "",
      color: "#" + Math.floor(Math.random() * 16777215).toString(16),
      dimensions: [
        {
          name: "",
          barValue: { type: "number", value: "" },
          lineValue: { type: "number", value: "" },
        },
      ],
    };
    onChange([...data, newCategory]);
  };

  const handleRemoveCategory = (categoryIndex: number) => {
    onChange(data.filter((_, i) => i !== categoryIndex));
  };

  const handleCategoryChange = (categoryIndex: number, field: keyof RadialCategory, value: any) => {
    const newData = [...data];
    newData[categoryIndex] = { ...newData[categoryIndex], [field]: value };
    onChange(newData);
  };

  const handleAddDimension = (categoryIndex: number) => {
    const newData = [...data];
    if (!newData[categoryIndex]) return;
    if (!Array.isArray(newData[categoryIndex].dimensions)) {
      newData[categoryIndex].dimensions = [] as any;
    }
    newData[categoryIndex].dimensions.push({
      name: "",
      barValue: { type: "number", value: "" },
      lineValue: { type: "number", value: "" },
    });
    onChange(newData);
  };

  const handleRemoveDimension = (categoryIndex: number, dimensionIndex: number) => {
    const newData = [...data];
    if (!newData[categoryIndex] || !Array.isArray(newData[categoryIndex].dimensions)) return;
    newData[categoryIndex].dimensions = newData[categoryIndex].dimensions.filter((_, i) => i !== dimensionIndex);
    onChange(newData);
  };

  const handleDimensionChange = (
    categoryIndex: number,
    dimensionIndex: number,
    field: keyof RadialDimension | 'barValue.type' | 'barValue.value' | 'lineValue.type' | 'lineValue.value',
    value: any
  ) => {
    const newData = [...data];
    if (!newData[categoryIndex]) return;
    if (!Array.isArray(newData[categoryIndex].dimensions)) {
      newData[categoryIndex].dimensions = [] as any;
    }
    if (!newData[categoryIndex].dimensions[dimensionIndex]) {
      newData[categoryIndex].dimensions[dimensionIndex] = {
        name: "",
        barValue: { type: "number", value: "" },
        lineValue: { type: "number", value: "" },
      } as any;
    }
    const dimension = newData[categoryIndex].dimensions[dimensionIndex];

    if (field === 'barValue.type') {
      dimension.barValue = {
        type: value,
        value: value === 'calculation' ? { source: 'calculation', id: null } : '',
      } as any;
      if (value !== 'form_variable') {
        dimension.barValueFormSource = undefined;
        dimension.barValueSelectedGroup = '';
        dimension.barValueSelectedQuestion = '';
        dimension.barValueMatrixRowId = '';
        dimension.barValueMatrixColumnId = '';
      }
    } else if (field === 'lineValue.type') {
      dimension.lineValue = {
        type: value,
        value: value === 'calculation' ? { source: 'calculation', id: null } : '',
      } as any;
      if (value !== 'form_variable') {
        dimension.lineValueFormSource = undefined;
        dimension.lineValueSelectedGroup = '';
        dimension.lineValueSelectedQuestion = '';
        dimension.lineValueMatrixRowId = '';
        dimension.lineValueMatrixColumnId = '';
      }
    } else if (field === 'barValue.value') {
      if (typeof dimension.barValue === 'object' && dimension.barValue !== null && 'type' in dimension.barValue) {
        if (dimension.barValue.type === 'calculation') {
          if (value && typeof value === 'object' && 'id' in value) {
            dimension.barValue.value = value;
          } else if (typeof value === 'string' && value.length > 0) {
            const parsedId = Number(value);
            dimension.barValue.value = {
              source: 'calculation',
              id: Number.isFinite(parsedId) ? parsedId : value,
            };
          } else {
            dimension.barValue.value = { source: 'calculation', id: null };
          }
        } else {
          dimension.barValue.value = value;
        }
      }
    } else if (field === 'lineValue.value') {
      if (typeof dimension.lineValue === 'object' && dimension.lineValue !== null && 'type' in dimension.lineValue) {
        if (dimension.lineValue.type === 'calculation') {
          if (value && typeof value === 'object' && 'id' in value) {
            dimension.lineValue.value = value;
          } else if (typeof value === 'string' && value.length > 0) {
            const parsedId = Number(value);
            dimension.lineValue.value = {
              source: 'calculation',
              id: Number.isFinite(parsedId) ? parsedId : value,
            };
          } else {
            dimension.lineValue.value = { source: 'calculation', id: null };
          }
        } else {
          dimension.lineValue.value = value;
        }
      }
    } else {
      (dimension as any)[field] = value;
    }

    onChange(newData);
  };

  const handleCalculationValueChange = (
    categoryIndex: number,
    dimensionIndex: number,
    fieldKey: 'barValue' | 'lineValue',
    calculationId: string
  ) => {
    const newData = [...data];
    if (!newData[categoryIndex] || !Array.isArray(newData[categoryIndex].dimensions)) return;
    const dimension = newData[categoryIndex].dimensions[dimensionIndex];
    if (!dimension) return;

    const target = dimension[fieldKey] as any;
    if (typeof target === 'object' && target !== null && 'type' in target) {
      const parsedId = calculationId ? Number(calculationId) : null;
      const safeId = parsedId !== null && Number.isFinite(parsedId) ? parsedId : null;
      target.value = {
        source: 'calculation',
        id: safeId,
      };
    }

    onChange(newData);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          Configura categorías (Manager, Mentor, Messenger) con sus dimensiones
        </p>
        <Button type="button" onClick={handleAddCategory} variant="outline" size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Agregar Categoría
        </Button>
      </div>

      {(data ?? []).map((category, categoryIndex) => (
        <Card key={categoryIndex} className="border-2">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <GripVertical className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-base flex-1">
                Categoría {categoryIndex + 1}
              </CardTitle>
              <Button
                type="button"
                size="icon"
                variant="destructive"
                onClick={() => handleRemoveCategory(categoryIndex)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Category Name and Color */}
            <div className="grid grid-cols-[1fr_200px] gap-3">
              <div className="space-y-2">
                <Label>Nombre de la Categoría</Label>
                <Input
                  value={category.name}
                  onChange={(e) => handleCategoryChange(categoryIndex, "name", e.target.value)}
                  placeholder="Ej: MANAGER - Excelencia"
                />
              </div>
              <div className="space-y-2">
                <EnhancedColorPicker
                  value={category.color}
                  onChange={(color) => handleCategoryChange(categoryIndex, "color", color)}
                  label="Color"
                />
              </div>
            </div>

            {/* Dimensions */}
            <div className="space-y-3 pt-3 border-t">
              <div className="flex justify-between items-center">
                <Label className="text-sm font-semibold">Dimensiones</Label>
                <Button
                  type="button"
                  onClick={() => handleAddDimension(categoryIndex)}
                  variant="outline"
                  size="sm"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Dimensión
                </Button>
              </div>

              {(category.dimensions ?? []).map((dimension, dimensionIndex) => {
                const barValueParts = getValueParts(dimension.barValue);
                const lineValueParts = getValueParts(dimension.lineValue);

                return (
                  <div key={dimensionIndex} className="p-3 bg-muted/50 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-medium text-muted-foreground">
                        Dimensión {dimensionIndex + 1}
                      </Label>
                      {category.dimensions.length > 1 && (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6"
                          onClick={() => handleRemoveDimension(categoryIndex, dimensionIndex)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>

                    {/* Dimension Name */}
                    <div className="space-y-1">
                      <Label className="text-xs">Nombre</Label>
                      <Input
                        value={dimension.name}
                        onChange={(e) =>
                          handleDimensionChange(categoryIndex, dimensionIndex, "name", e.target.value)
                        }
                        placeholder="Ej: ESTRATEGIA"
                        className="text-sm"
                      />
                    </div>

                    {/* Bar Value */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Label className="text-xs">Valor Barra (Usuario)</Label>
                        {barValueParts.type === "form_variable" && dimension.barValueFormSource && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            {dimension.barValueFormSource === "main" ? (
                              <>
                                <FileText className="h-3 w-3" />
                                <span>Principal</span>
                              </>
                            ) : (
                              <>
                                <Users className="h-3 w-3" />
                                <span>Referidos</span>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Select
                          value={barValueParts.type}
                          onValueChange={(value) =>
                            handleDimensionChange(categoryIndex, dimensionIndex, "barValue.type", value)
                          }
                        >
                          <SelectTrigger className="text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="number">Número</SelectItem>
                            <SelectItem value="form_variable">Variable Form</SelectItem>
                            <SelectItem value="calculation">Cálculo</SelectItem>
                          </SelectContent>
                        </Select>
                        {barValueParts.type === "form_variable" ? (
                          <div className="space-y-2">
                            <Select
                              value={dimension.barValueFormSource || "main"}
                              onValueChange={(value) =>
                                handleDimensionChange(categoryIndex, dimensionIndex, "barValueFormSource", value)
                              }
                            >
                              <SelectTrigger className="text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="main">Formulario Principal</SelectItem>
                                <SelectItem value="referral">Formulario Referidos</SelectItem>
                              </SelectContent>
                            </Select>
                            <div className="space-y-2">
                              <HierarchicalGroupSelector
                                selectedGroup={dimension.barValueSelectedGroup || ''}
                                selectedQuestion={dimension.barValueSelectedQuestion || ''}
                                onGroupChange={(groupPath) => {
                                  handleDimensionChange(categoryIndex, dimensionIndex, "barValueSelectedGroup", groupPath);
                                  // Reset question when group changes
                                  handleDimensionChange(categoryIndex, dimensionIndex, "barValueSelectedQuestion", "");
                                  handleDimensionChange(categoryIndex, dimensionIndex, "barValue.value", "");
                                  handleDimensionChange(categoryIndex, dimensionIndex, "barValueMatrixRowId", "");
                                  handleDimensionChange(categoryIndex, dimensionIndex, "barValueMatrixColumnId", "");
                                }}
                                onQuestionChange={(questionName, questionText) => {
                                  handleDimensionChange(categoryIndex, dimensionIndex, "barValueSelectedQuestion", questionName);
                                  handleDimensionChange(categoryIndex, dimensionIndex, "barValue.value", questionName);
                                }}
                                jotformFormId={dimension.barValueFormSource === "referral" ? jotformFormIdReferral : jotformFormId}
                                showQuestionSelector={true}
                                questionSelectorType="select"
                                className="text-sm"
                              />
                              
                              {/* Matrix Row and Column Selection - Bar Value */}
                              {dimension.barValueSelectedQuestion && isMatrixQuestion(dimension.barValueSelectedQuestion, dimension.barValueFormSource || "main") && (
                                <div className="mt-2">
                                  <MatrixRowSelector
                                    questionName={dimension.barValueSelectedQuestion}
                                    jotformFormId={(dimension.barValueFormSource || "main") === "referral" ? referralFormId : mainFormId}
                                    selectedRowId={dimension.barValueMatrixRowId || ""}
                                    selectedColumnId={dimension.barValueMatrixColumnId || ""}
                                    onRowChange={(rowId, rowText) => {
                                      handleDimensionChange(categoryIndex, dimensionIndex, "barValueMatrixRowId", rowId);
                                      handleDimensionChange(categoryIndex, dimensionIndex, "barValueMatrixRowText", rowText);
                                    }}
                                    onColumnChange={(columnId, columnText) => {
                                      handleDimensionChange(categoryIndex, dimensionIndex, "barValueMatrixColumnId", columnId);
                                      handleDimensionChange(categoryIndex, dimensionIndex, "barValueMatrixColumnText", columnText);
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        ) : barValueParts.type === "calculation" ? (
                          <Select
                            value={barValueParts.value}
                            onValueChange={(value) =>
                              handleCalculationValueChange(categoryIndex, dimensionIndex, 'barValue', value)
                            }
                          >
                            <SelectTrigger className="text-sm">
                              <SelectValue placeholder="Selecciona un cálculo" />
                            </SelectTrigger>
                            <SelectContent>
                              {calculations && calculations.length > 0 ? (
                                calculations.map((calc) => (
                                  <SelectItem key={`bar-calc-${categoryIndex}-${dimensionIndex}-${calc.id}`} value={calc.id.toString()}>
                                    <div className="flex flex-col">
                                      <span className="font-medium">{calc.name}</span>
                                      <span className="text-xs text-muted-foreground">
                                        {calc.calculation_type === 'aggregate' ? 'Agregación' : 'Fórmula'} • {calc.config?.operation || 'N/A'}
                                      </span>
                                    </div>
                                  </SelectItem>
                                ))
                              ) : (
                                <SelectItem value="__no_calculations" disabled>
                                  No hay cálculos disponibles
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            value={barValueParts.value}
                            onChange={(e) =>
                              handleDimensionChange(categoryIndex, dimensionIndex, "barValue.value", e.target.value)
                            }
                            placeholder={getPlaceholder(barValueParts.type)}
                            className="text-sm"
                          />
                        )}
                      </div>
                    </div>

                    {/* Line Value */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Label className="text-xs">Valor Línea (Referido)</Label>
                        {lineValueParts.type === "form_variable" && dimension.lineValueFormSource && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            {dimension.lineValueFormSource === "main" ? (
                              <>
                                <FileText className="h-3 w-3" />
                                <span>Principal</span>
                              </>
                            ) : (
                              <>
                                <Users className="h-3 w-3" />
                                <span>Referidos</span>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Select
                          value={lineValueParts.type}
                          onValueChange={(value) =>
                            handleDimensionChange(categoryIndex, dimensionIndex, "lineValue.type", value)
                          }
                        >
                          <SelectTrigger className="text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="number">Número</SelectItem>
                            <SelectItem value="form_variable">Variable Form</SelectItem>
                            <SelectItem value="calculation">Cálculo</SelectItem>
                          </SelectContent>
                        </Select>
                        {lineValueParts.type === "form_variable" ? (
                          <div className="space-y-2">
                            <Select
                              value={dimension.lineValueFormSource || "main"}
                              onValueChange={(value) =>
                                handleDimensionChange(categoryIndex, dimensionIndex, "lineValueFormSource", value)
                              }
                            >
                              <SelectTrigger className="text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="main">Formulario Principal</SelectItem>
                                <SelectItem value="referral">Formulario Referidos</SelectItem>
                              </SelectContent>
                            </Select>
                            <div className="space-y-2">
                              <HierarchicalGroupSelector
                                selectedGroup={dimension.lineValueSelectedGroup || ''}
                                selectedQuestion={dimension.lineValueSelectedQuestion || ''}
                                onGroupChange={(groupPath) => {
                                  handleDimensionChange(categoryIndex, dimensionIndex, "lineValueSelectedGroup", groupPath);
                                  // Reset question when group changes
                                  handleDimensionChange(categoryIndex, dimensionIndex, "lineValueSelectedQuestion", "");
                                  handleDimensionChange(categoryIndex, dimensionIndex, "lineValue.value", "");
                                  handleDimensionChange(categoryIndex, dimensionIndex, "lineValueMatrixRowId", "");
                                  handleDimensionChange(categoryIndex, dimensionIndex, "lineValueMatrixColumnId", "");
                                }}
                                onQuestionChange={(questionName, questionText) => {
                                  handleDimensionChange(categoryIndex, dimensionIndex, "lineValueSelectedQuestion", questionName);
                                  handleDimensionChange(categoryIndex, dimensionIndex, "lineValue.value", questionName);
                                }}
                                jotformFormId={dimension.lineValueFormSource === "referral" ? jotformFormIdReferral : jotformFormId}
                                showQuestionSelector={true}
                                questionSelectorType="select"
                                className="text-sm"
                              />
                              
                              {/* Matrix Row and Column Selection - Line Value */}
                              {dimension.lineValueSelectedQuestion && isMatrixQuestion(dimension.lineValueSelectedQuestion, dimension.lineValueFormSource || "main") && (
                                <div className="mt-2">
                                  <MatrixRowSelector
                                    questionName={dimension.lineValueSelectedQuestion}
                                    jotformFormId={(dimension.lineValueFormSource || "main") === "referral" ? referralFormId : mainFormId}
                                    selectedRowId={dimension.lineValueMatrixRowId || ""}
                                    selectedColumnId={dimension.lineValueMatrixColumnId || ""}
                                    onRowChange={(rowId, rowText) => {
                                      handleDimensionChange(categoryIndex, dimensionIndex, "lineValueMatrixRowId", rowId);
                                      handleDimensionChange(categoryIndex, dimensionIndex, "lineValueMatrixRowText", rowText);
                                    }}
                                    onColumnChange={(columnId, columnText) => {
                                      handleDimensionChange(categoryIndex, dimensionIndex, "lineValueMatrixColumnId", columnId);
                                      handleDimensionChange(categoryIndex, dimensionIndex, "lineValueMatrixColumnText", columnText);
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        ) : lineValueParts.type === "calculation" ? (
                          <Select
                            value={lineValueParts.value}
                            onValueChange={(value) =>
                              handleCalculationValueChange(categoryIndex, dimensionIndex, 'lineValue', value)
                            }
                          >
                            <SelectTrigger className="text-sm">
                              <SelectValue placeholder="Selecciona un cálculo" />
                            </SelectTrigger>
                            <SelectContent>
                              {calculations && calculations.length > 0 ? (
                                calculations.map((calc) => (
                                  <SelectItem key={`line-calc-${categoryIndex}-${dimensionIndex}-${calc.id}`} value={calc.id.toString()}>
                                    <div className="flex flex-col">
                                      <span className="font-medium">{calc.name}</span>
                                      <span className="text-xs text-muted-foreground">
                                        {calc.calculation_type === 'aggregate' ? 'Agregación' : 'Fórmula'} • {calc.config?.operation || 'N/A'}
                                      </span>
                                    </div>
                                  </SelectItem>
                                ))
                              ) : (
                                <SelectItem value="__no_calculations" disabled>
                                  No hay cálculos disponibles
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            value={lineValueParts.value}
                            onChange={(e) =>
                              handleDimensionChange(categoryIndex, dimensionIndex, "lineValue.value", e.target.value)
                            }
                            placeholder={getPlaceholder(lineValueParts.type)}
                            className="text-sm"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}

      {data.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <p>No hay categorías configuradas</p>
          <Button type="button" onClick={handleAddCategory} variant="outline" className="mt-4">
            <Plus className="h-4 w-4 mr-2" />
            Agregar Primera Categoría
          </Button>
        </div>
      )}

      {/* Help text */}
      <div className="text-xs text-muted-foreground space-y-1 p-3 bg-muted/30 rounded">
        <p><strong>Número:</strong> Valor numérico directo (ej: 7.5)</p>
        <p><strong>Variable PPT:</strong> Nombre de variable extraída del PowerPoint</p>
        <p><strong>Variable Formulario:</strong> JSONPath de pregunta del formulario</p>
        <p><strong>Cálculo:</strong> Nombre de un cálculo configurado</p>
      </div>
    </div>
  );
}

function getPlaceholder(type: string): string {
  switch (type) {
    case "number":
      return "9.2";
    case "ppt_variable":
      return "var_estrategia";
    case "form_variable":
      return "answers.q1";
    case "calculation":
      return "promedio_estrategia";
    default:
      return "";
  }
}
